import { Link, router } from "@inertiajs/react"
import { BackofficeLayout } from "@/components/layout/BackofficeLayout"
import { SearchInput } from "@/components/shared/SearchInput"
import { Pagination, PaginationMeta } from "@/components/shared/Pagination"
import { Badge } from "@/components/ui/badge"
import type { NotarySummary } from "@/types"

function StatusBadge({ status }: { status: NotarySummary['status'] }) {
  const config: Record<string, { label: string; className: string }> = {
    pending: { label: "Pending", className: "bg-yellow-100 text-yellow-800" },
    approved: { label: "Approved", className: "bg-green-100 text-green-800" },
    rejected: { label: "Rejected", className: "bg-red-100 text-red-800" },
    suspended: { label: "Suspended", className: "bg-gray-100 text-gray-800" },
  }
  const { label, className } = config[status] || { label: status, className: "bg-secondary text-secondary-foreground" }
  return <Badge className={`font-normal text-xs ${className}`}>{label}</Badge>
}

type Props = {
  notaries: NotarySummary[]
  search: string
  status_filter: string
  pagination: PaginationMeta
}

export default function BackofficeNotariesIndex({ notaries, search, status_filter, pagination }: Props) {
  return (
    <BackofficeLayout title="Notaries" breadcrumbs={[{ label: "Backoffice", href: "/backoffice/dashboard" }, { label: "Notaries" }]}>
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight">Notaries</h1>
        <p className="text-sm text-muted-foreground">{pagination.total} {pagination.total === 1 ? "notary" : "notaries"}</p>
      </div>

      <div className="flex items-center gap-3">
        <SearchInput value={search} placeholder="Search notaries..." />
        <div className="flex items-center gap-1.5">
          {['all', 'pending', 'approved', 'rejected', 'suspended'].map((s) => (
            <button
              key={s}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                (status_filter || 'all') === s
                  ? 'bg-foreground text-background font-medium'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              onClick={() => router.get('/backoffice/notaries', { search, status: s === 'all' ? '' : s }, { preserveState: true })}
            >
              <span className="capitalize">{s}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        {notaries.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <h3 className="text-sm font-medium text-foreground mb-1">No notaries found</h3>
          </div>
        ) : (
          <div className="border rounded-lg">
            {notaries.map((n, i) => (
              <div
                key={n.id}
                className={`px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${i > 0 ? "border-t" : ""}`}
                onClick={() => router.visit(`/backoffice/notaries/${n.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Link href={`/backoffice/notaries/${n.id}`} className="text-sm font-medium hover:underline">{n.full_name}</Link>
                      <span className="text-[12px] text-muted-foreground">{n.email_address}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Commission: {n.commission_state} · #{n.commission_number} · Expires {new Date(n.commission_expiry).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} · {n.session_modes.join(", ")}
                    </div>
                  </div>
                  <div className="ml-4">
                    <StatusBadge status={n.status} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  )
}
